import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import Modal from 'react-modal';
import styles from './CashierBonusAnalytics.module.css';
import cashierBonusApi from '../../../../store/analyticsStores/cashierBonusStore/cashierBonusApi';

const modalStyles = {
    overlay: {
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
    },
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: 'none',
        padding: 0,
        borderRadius: 16,
        maxWidth: 560,
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
    },
};

function formatTimestamp(ts) {
    if (!ts) return { date: '-', time: '-' };
    const d = new Date(ts);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return { date: `${day}.${month}.${year}`, time: `${hours}:${minutes}` };
}

function getBonusSource(tx, t) {
    if (tx.has_auto_notification) return t('NSCashierBonuses.sourceAutoNotification');
    if (tx.sale_type === 'certificate') return t('NSCashierBonuses.sourceCertificate');
    if (tx.sale_title) return tx.sale_title;
    if (tx.type === 'BONUS') return t('NSCashierBonuses.sourceBonus');
    if (tx.type === 'BIRTHDAY_BONUS') return t('NSCashierBonuses.sourceBirthday');
    if (tx.type === 'CERTIFICATE') return t('NSCashierBonuses.sourceCertificate');
    if (tx.type === 'DEPOSIT') return t('NSCashierBonuses.sourceDeposit');
    return t('NSCashierBonuses.sourceTransaction');
}

function getStatusLabel(status, t) {
    switch (status) {
        case 'COMPLETED': return t('NSCashierBonuses.statusCompleted');
        case 'PENDING': return t('NSCashierBonuses.statusPending');
        case 'REFUNDED': return t('NSCashierBonuses.statusRefunded');
        default: return status;
    }
}

function getStatusClass(status) {
    switch (status) {
        case 'COMPLETED': return styles.statusCompleted;
        case 'PENDING': return styles.statusPending;
        case 'REFUNDED': return styles.statusRefunded;
        default: return '';
    }
}

function getTotalBonusUsed(tx) {
    return (tx.paid_from_private_balance || 0)
        + (tx.paid_from_birthday || 0)
        + (tx.paid_from_auto_mailing || 0)
        + (tx.paid_from_mailing || 0)
        + (tx.paid_from_manual || 0)
        + (tx.paid_from_cross || 0)
        + (tx.paid_from_review || 0)
        + (tx.paid_from_referral || 0)
        + (tx.paid_from_sale || 0)
        + (tx.paid_from_revert || 0)
        + (tx.paid_from_certificate || 0);
}

function DetailRow({ label, value, highlight }) {
    if (value === undefined || value === null || value === '' || value === '-' || value === 0) return null;
    return (
        <div className={styles.modalDetailRow}>
            <span className={styles.modalDetailLabel}>{label}</span>
            <span className={highlight ? styles.modalDetailValueHighlight : styles.modalDetailValue}>{value}</span>
        </div>
    );
}

function TransactionModal({ tx, isOpen, onClose, t }) {
    if (!tx) return null;
    const { date, time } = formatTimestamp(tx.created_at);
    const bonusUsed = getTotalBonusUsed(tx);

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyles} ariaHideApp={false}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>{t('NSCashierBonuses.transactionDetails')}</h3>
                    <button className={styles.modalClose} onClick={onClose}>&times;</button>
                </div>
                <div className={styles.modalBody}>
                    <DetailRow label={t('NSCashierBonuses.colAmount')} value={tx.order_total_amount ? `${Math.round(tx.order_total_amount).toLocaleString()} ₸` : null} />
                    <DetailRow label={t('NSCashierBonuses.colStatus')} value={
                        <span className={getStatusClass(tx.status)}>{getStatusLabel(tx.status, t)}</span>
                    } />
                    <DetailRow label={t('NSCashierBonuses.colInitialBalance')} value={tx.sender_balance ? `${Math.round(tx.sender_balance).toLocaleString()} ₸` : null} />
                    <DetailRow label={t('NSCashierBonuses.colBonusUsed')} value={bonusUsed > 0 ? `${Math.round(bonusUsed).toLocaleString()} ₸` : null} highlight />
                    <DetailRow label={t('NSCashierBonuses.colBonusSize')} value={tx.order_cashback_amount ? `${Math.round(tx.order_cashback_amount).toLocaleString()} ₸` : null} />
                    <DetailRow label={t('NSCashierBonuses.colBonusSource')} value={getBonusSource(tx, t)} />
                    <DetailRow label={t('NSCashierBonuses.colPaymentType')} value={tx.payment_method || tx.type} />
                    <DetailRow label={t('NSCashierBonuses.colRecipientPhone')} value={tx.sender_phone || null} />
                    <DetailRow label={t('NSCashierBonuses.colDate')} value={date !== '-' ? `${date} ${time}` : null} />
                    <DetailRow label={t('NSCashierBonuses.colAward')} value={tx.award_give_award ? '✓' : null} />
                    <DetailRow label={t('NSCashierBonuses.colStamps')} value={tx.award_stamp_count > 0 ? tx.award_stamp_count : null} />
                    <DetailRow label={t('NSCashierBonuses.colComment')} value={tx.comment || null} />
                    {tx.sender_name && (
                        <DetailRow label={t('NSCashierBonuses.clientName')} value={tx.sender_name} />
                    )}
                    {tx.paid_from_birthday > 0 && (
                        <DetailRow label={t('NSCashierBonuses.paidFromBirthday')} value={`${Math.round(tx.paid_from_birthday).toLocaleString()} ₸`} />
                    )}
                    {tx.paid_from_certificate > 0 && (
                        <DetailRow label={t('NSCashierBonuses.paidFromCertificate')} value={`${Math.round(tx.paid_from_certificate).toLocaleString()} ₸`} />
                    )}
                    {tx.paid_from_auto_mailing > 0 && (
                        <DetailRow label={t('NSCashierBonuses.paidFromAutoMailing')} value={`${Math.round(tx.paid_from_auto_mailing).toLocaleString()} ₸`} />
                    )}
                    {tx.paid_from_manual > 0 && (
                        <DetailRow label={t('NSCashierBonuses.paidFromManual')} value={`${Math.round(tx.paid_from_manual).toLocaleString()} ₸`} />
                    )}
                    {tx.paid_from_referral > 0 && (
                        <DetailRow label={t('NSCashierBonuses.paidFromReferral')} value={`${Math.round(tx.paid_from_referral).toLocaleString()} ₸`} />
                    )}
                </div>
            </div>
        </Modal>
    );
}

function SortableHeader({ field, label, sortBy, sortDir, onSort }) {
    const isActive = sortBy === field;
    const arrow = isActive ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
    return (
        <th className={styles.sortableHeader} onClick={() => onSort(field)}>
            {label}{arrow}
        </th>
    );
}

export default function CashierTransactionDetails({
    cashier,
    transactionsData,
    isLoading,
    currentPage,
    onPageChange,
    onBack,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    branchId,
    sortBy,
    sortDir,
    onSort,
}) {
    const { t } = useTranslation();
    const [selectedTx, setSelectedTx] = useState(null);
    const transactions = transactionsData?.transactions || [];
    const total = transactionsData?.total || 0;
    const limit = transactionsData?.limit || 50;
    const totalPages = Math.ceil(total / limit);

    const formatDateStr = (date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1) < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
        const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
        return `${year}-${month}-${day}`;
    };

    const handleExport = () => {
        if (!branchId) return;
        let dates = '';
        if (startDate && endDate) {
            dates = `?from=${formatDateStr(startDate)}&to=${formatDateStr(endDate)}`;
        }
        cashierBonusApi.exportCashierTransactions(branchId, cashier.casher_phone, cashier.waiter_id, dates);
    };

    const handleClearDates = () => {
        setStartDate(null);
        setEndDate(null);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <div className={styles.pagination}>
                <div className={styles.paginationInfo}>
                    {t('NSCashierBonuses.showing')} {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, total)} {t('NSCashierBonuses.of')} {total}
                </div>
                <div className={styles.paginationButtons}>
                    <button
                        className={styles.paginationButton}
                        disabled={currentPage === 1}
                        onClick={() => onPageChange(currentPage - 1)}
                    >
                        ←
                    </button>
                    {startPage > 1 && (
                        <>
                            <button className={styles.paginationButton} onClick={() => onPageChange(1)}>1</button>
                            {startPage > 2 && <span className={styles.paginationEllipsis}>...</span>}
                        </>
                    )}
                    {pages.map(p => (
                        <button
                            key={p}
                            className={`${styles.paginationButton} ${p === currentPage ? styles.paginationButtonActive : ''}`}
                            onClick={() => onPageChange(p)}
                        >
                            {p}
                        </button>
                    ))}
                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && <span className={styles.paginationEllipsis}>...</span>}
                            <button className={styles.paginationButton} onClick={() => onPageChange(totalPages)}>{totalPages}</button>
                        </>
                    )}
                    <button
                        className={styles.paginationButton}
                        disabled={currentPage === totalPages}
                        onClick={() => onPageChange(currentPage + 1)}
                    >
                        →
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.detailHeader}>
                <button className={styles.backButton} onClick={onBack}>
                    ← {t('NSCashierBonuses.back')}
                </button>
                <h2 className={styles.detailTitle}>
                    {cashier.casher_name || cashier.casher_phone}
                    {cashier.casher_role && (
                        <span className={styles.roleBadge} data-role={cashier.casher_role}>
                            {t('roles.' + cashier.casher_role)}
                        </span>
                    )}
                </h2>
            </div>

            <div className={styles.datePickerRow}>
                <div className={styles.datePickers}>
                    <DatePicker
                        selected={startDate}
                        onChange={date => setStartDate(date)}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        dateFormat="dd.MM.yyyy"
                        placeholderText={t('from')}
                        className={styles.datePicker}
                    />
                    <span className={styles.dateSeparator}>—</span>
                    <DatePicker
                        selected={endDate}
                        onChange={date => setEndDate(date)}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        dateFormat="dd.MM.yyyy"
                        placeholderText={t('to')}
                        className={styles.datePicker}
                    />
                </div>
                {(startDate || endDate) && (
                    <button className={styles.clearButton} onClick={handleClearDates}>
                        {t('clear')}
                    </button>
                )}
                <button className={styles.exportButton} onClick={handleExport}>
                    {t('NSCashierBonuses.export')}
                </button>
            </div>

            {isLoading ? (
                <div className={styles.loading}>{t('loading')}...</div>
            ) : transactions.length === 0 ? (
                <div className={styles.noData}>{t('NSCashierBonuses.noData')}</div>
            ) : (
                <div className={styles.tableCard}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <SortableHeader field="order_total_amount" label={t('NSCashierBonuses.colAmount')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                                <th>{t('NSCashierBonuses.colStatus')}</th>
                                <SortableHeader field="order_cashback_amount" label={t('NSCashierBonuses.colBonusSize')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                                <th>{t('NSCashierBonuses.colPaymentType')}</th>
                                <th>{t('NSCashierBonuses.colRecipientPhone')}</th>
                                <SortableHeader field="created_at" label={t('NSCashierBonuses.colDate')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map((tx, index) => {
                                const { date, time } = formatTimestamp(tx.created_at);
                                const rowNum = (currentPage - 1) * limit + index + 1;

                                return (
                                    <tr
                                        key={tx.id || index}
                                        className={styles.clickableRow}
                                        onClick={() => setSelectedTx(tx)}
                                    >
                                        <td>{rowNum}</td>
                                        <td>{tx.order_total_amount ? Math.round(tx.order_total_amount).toLocaleString() : '-'}</td>
                                        <td>
                                            <span className={getStatusClass(tx.status)}>
                                                {getStatusLabel(tx.status, t)}
                                            </span>
                                        </td>
                                        <td className={styles.bonusAmount}>
                                            {tx.order_cashback_amount ? Math.round(tx.order_cashback_amount).toLocaleString() : '-'}
                                        </td>
                                        <td>{tx.payment_method || tx.type}</td>
                                        <td>{tx.sender_phone || '-'}</td>
                                        <td>{date} <span className={styles.textMuted}>{time}</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {renderPagination()}
                </div>
            )}

            <TransactionModal
                tx={selectedTx}
                isOpen={!!selectedTx}
                onClose={() => setSelectedTx(null)}
                t={t}
            />
        </div>
    );
}
